import {
  Job as JobProto,
  Member as MemberProto,
  MemberRoleType,
  MemberType,
  SearchableJob as SearchableJobProto,
} from '@taxfyle/api-internal/internal/job_pb'
import {
  BoolValue,
  StringValue,
} from 'google-protobuf/google/protobuf/wrappers_pb'
import type { PrimeTeamFn, PrimeWorkspaceFn } from '@/features/iam'
import type { PrimeConversationFn } from '@/features/messaging'
import { Id } from '@/infra/id'
import {
  decimalToNumber,
  maybeDecimalToNumber,
  timestampToDate,
} from '@/utils/grpc-util'
import { generateAvatarFallback } from '@/utils/image-util'
import { match } from '@/utils/pattern-match-util'
import { type Job } from '@/features/jobs/Job'
import {
  mapJobStatusProtoToJobStatus,
  mapResolutionProtoToResolution,
  mapWaitingOnProtoToWaitingOn,
} from '@/features/jobs/job-mappers'
import { mapJobScopeChangeStatusProtoToJobScopeChangeStatus } from '@/features/jobs/job-scope-changes/job-scope-change-mappers'
import { LegendVersionId } from '@/features/jobs/legends/PublishedLegend'
import { type PrimePublishedLegendFn } from '@/features/jobs/legends/PublishedLegendStore'
import { mapJobProgressionStepStatusProtoToStatus } from '@/features/jobs/taxes/progression/job-progression-mappers'
import {
  type SearchableJobProps,
  type SearchableMember,
} from '@/features/jobs/list/SearchableJob'

/**
 * What mapping a searchable job needs to resolve its references.
 */
export interface SearchableJobMappingContext {
  primeWorkspace: PrimeWorkspaceFn
  primeConversation: PrimeConversationFn
  primePublishedLegend: PrimePublishedLegendFn
  currentUserId: Id
}

export function mapSearchableJobProtoToProps(
  proto: SearchableJobProto,
  context: SearchableJobMappingContext,
): Partial<SearchableJobProps> {
  const legendId = proto.getLegendId()
  const legendVersion = proto.getLegendVersion()
  if (legendId && legendVersion) {
    context.primePublishedLegend(LegendVersionId(legendId, legendVersion))
  }

  const workspaceId = proto.getWorkspaceId()
  const conversationId = proto.getConversationId()
  const status = proto.getStatus()
  const progressionStep = proto.getProgressionStep()
  const providerCutAmount = proto.getProviderCutAmount()

  return {
    legendName: proto.getLegendName(),
    legendId: Id(legendId),
    legendVersion,
    dateDeadline: timestampToDate(proto.getDateDeadline()),
    dateCreated: timestampToDate(proto.getDateCreated()),
    resolution: mapResolutionProtoToResolution(proto.getResolution()),
    dateSubmitted: timestampToDate(proto.getDateSubmitted()),
    dateAccepted: timestampToDate(proto.getDateClaimed()),
    dateClosed: timestampToDate(proto.getDateCompleted()),
    dateModified: timestampToDate(proto.getDateModified()),
    dateLastProMessageActivity: timestampToDate(
      proto.getDateLastProMessageActivity(),
    ),
    dateLastClientMessageActivity: timestampToDate(
      proto.getDateLastClientMessageActivity(),
    ),
    dateLastRelationshipManagerMessageActivity: timestampToDate(
      proto.getDateLastRelationshipManagerMessageActivity(),
    ),
    dateLastActivity: timestampToDate(proto.getDateLastActivity()),
    dateAdvanceAllowed: timestampToDate(proto.getAdvanceAllowedTime()),
    id: proto.getId(),
    workspace: workspaceId
      ? context.primeWorkspace({ id: workspaceId })
      : undefined,
    conversation: conversationId
      ? context.primeConversation({ id: conversationId.getValue() })
      : null,
    shortId: proto.getShortId(),
    name: proto.getTitle(),
    description: proto.getDescription(),
    status: status ? mapJobStatusProtoToJobStatus(status) : undefined,
    progressionStep: progressionStep
      ? mapJobProgressionStepStatusProtoToStatus(progressionStep)
      : undefined,
    waitingOn: mapWaitingOnProtoToWaitingOn(proto.getWaitingOn()),
    members: proto
      .getMembersList()
      .map((m) =>
        mapSearchableMemberProtoToSearchableMember(m, workspaceId, context),
      ),
    scopeChangeStatus: mapJobScopeChangeStatusProtoToJobScopeChangeStatus(
      proto.getScopeChangeStatus(),
    ),
    jobScopeItems: proto.getJobScopeItemsList(),
    clientTeamName: proto.getClientTeamName(),
    clientTeamId: Id(proto.getClientTeamId()),
    proTeamName: proto.getProTeamName(),
    proTeamId: Id(proto.getProTeamId()),
    providerCutAmount: providerCutAmount
      ? decimalToNumber(providerCutAmount)
      : undefined,
    total: maybeDecimalToNumber(proto.getTotal()),
    archived: proto.getArchived(),
    isRequestedPro: proto.getIsRequestedPro()?.getValue(),
  }
}

function mapSearchableMemberProtoToSearchableMember(
  member: MemberProto,
  workspaceId: Id,
  context: SearchableJobMappingContext,
): SearchableMember {
  const id = member.getId()
  const displayName = member.getDisplayName()
  return {
    id,
    displayName,
    email: member.getEmail()?.getValue(),
    avatar: member.getPicture(),
    isCurrentUser: Id.some(id) && id === context.currentUserId,
    type: match(member.getType(), {
      [MemberType.MEMBER_TYPE_UNSPECIFIED]: () => 'Unspecified',
      [MemberType.MEMBER_TYPE_PROVIDER]: () => 'Pro',
      [MemberType.MEMBER_TYPE_CLIENT]: () => 'Client',
    }),
    role: match(member.getRole(), {
      [MemberRoleType.MEMBER_ROLE_TYPE_UNSPECIFIED]: () => 'Unspecified',
      [MemberRoleType.SUPPORTING]: () => 'Supporting',
      [MemberRoleType.CHAMPION]: () => 'Champion',
    }),
    avatarFallback: generateAvatarFallback(
      displayName,
      context.primeWorkspace({ id: workspaceId })?.branding.colors.primary,
    ),
  }
}

/**
 * Reshapes a job from a realtime event into the searchable job the search
 * endpoint would have returned for it.
 */
export function mapJobProtoToSearchableJobProto(
  proto: JobProto,
  primeTeam: PrimeTeamFn,
  primeWorkspace: PrimeWorkspaceFn,
): SearchableJobProto {
  const jobMembers = proto.getMembersList()
  const clientTeamId = jobMembers
    .find((m) => m.getTeamId() && m.getType() === JobProto.MemberType.CLIENT)
    ?.getTeamId()
  const proTeamId = jobMembers
    .find((m) => m.getTeamId() && m.getType() === JobProto.MemberType.PROVIDER)
    ?.getTeamId()
  const members = jobMembers.map((m) =>
    new MemberProto()
      .setId(m.getId())
      .setDisplayName(m.getDisplayName())
      .setEmail(new StringValue().setValue(m.getEmail()))
      .setPicture(m.getPicture())
      .setType(
        match(m.getType(), {
          [JobProto.MemberType.CLIENT]: () => MemberType.MEMBER_TYPE_CLIENT,
          [JobProto.MemberType.PROVIDER]: () => MemberType.MEMBER_TYPE_PROVIDER,
        }),
      )
      .setRole(
        match(m.getRole(), {
          [JobProto.MemberRole.CHAMPION]: () => MemberRoleType.CHAMPION,
          [JobProto.MemberRole.SUPPORTING]: () => MemberRoleType.SUPPORTING,
        }),
      ),
  )
  const legend = proto.getLegend()

  return (
    new SearchableJobProto()
      .setLegendName(legend?.getName() ?? '')
      .setLegendId(legend?.getId() ?? '')
      .setLegendVersion(legend?.getVersion() ?? 0)
      .setTotal(proto.getTotal())
      .setJobScopeItemsList(
        proto.getJobScopeItemsList().map((i) => i.getName()),
      )
      .setIsRequestedPro(
        proto.getRequestedProPublicId()
          ? new BoolValue().setValue(true)
          : undefined,
      )
      .setRatingSubmitted(proto.getRatingSubmitted())
      .setDateDeadline(proto.getDateDeadline())
      .setDateCreated(proto.getDateCreated())
      .setResolution(proto.getResolution())
      .setDateSubmitted(proto.getDateTransmitted())
      .setDateClaimed(proto.getDateAccepted())
      .setDateCompleted(proto.getDateClosed())
      .setDateModified(proto.getDateModified())
      .setDateLastActivity(proto.getDateLastActivity())
      .setAdvanceAllowedTime(proto.getAdvanceAllowedTime())
      .setId(proto.getId())
      .setWorkspaceId(proto.getWorkspaceId())
      .setShortId(proto.getShortId())
      .setTitle(proto.getName()?.getValue() ?? '')
      // The computed job title.
      .setDescription(proto.getDescription()?.getValue() || '')
      .setStatus(proto.getStatus())
      .setWaitingOn(proto.getWaitingOn())
      .setConversationId(proto.getConversationId())
      .setMembersList(members)
      .setClientTeamId(clientTeamId ?? '')
      .setProTeamId(proTeamId ?? '')
      .setClientTeamName(primeTeam({ id: clientTeamId })?.name ?? '')
      .setProTeamName(primeTeam({ id: proTeamId })?.name ?? '')
      .setWorkspaceName(
        primeWorkspace({ id: proto.getWorkspaceId() })?.name ?? '',
      )
      .setJobSpecsList(proto.getJobSpecsList())
      .setProviderCutAmount(proto.getProviderCutAmount())
      .setArchived(proto.getArchived())
      .setProgressionStep(proto.getProgressionStep())
      .setScopeChangeStatus(proto.getScopeChangeStatus())
      .setIsExtensionOnlyJob(
        proto.getType()?.getTax()?.getIsExtensionOnlyJob() ?? false,
      )
  )
}

/**
 * Maps a job changed locally to the searchable job fields it updates.
 */
export function mapJobToSearchableJobProps(
  job: Job,
): Partial<SearchableJobProps> {
  const members: SearchableMember[] = job.collaborators.map((c) => {
    return {
      id: Id.assert(c.member.user.publicId),
      displayName: c.member.displayName,
      avatar: c.member.picture,
      avatarFallback: c.member.avatarFallback,
      type: c.type,
      role: c.role,
      isCurrentUser: c.member.isCurrentUser,
    }
  })

  return {
    shortId: Id.assert(job.shortId),
    workspace: job.workspace,
    legendName: job.legendName,
    dateDeadline: job.dateDeadline,
    dateCreated: job.dateCreated,
    dateSubmitted: job.dateSubmitted,
    dateAccepted: job.dateAccepted,
    dateClosed: job.dateClosed,
    dateLastActivity: job.dateLastActivity,
    dateAdvanceAllowed: job.dateAdvanceAllowed,
    resolution: job.resolution,
    name: job.displayName,
    status: job.status,
    waitingOn: job.waitingOn,
    conversation: job.conversation,
    clientTeamName: job.clientTeamName,
    proTeamName: job.proTeamName,
    providerCutAmount: job.providerCutAmount,
    members,
  }
}
